import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rahmani30-collaborate-api.rahmani30reactapp.workers.dev/api';

let _accessToken: string | null = (() => {
  try { return localStorage.getItem('accessToken'); } catch { return null; }
})();

export function setAccessToken(token: string | null): void {
  _accessToken = token;
  try {
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  } catch (_) { }
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function setRefreshToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem('refreshToken', token);
    } else {
      localStorage.removeItem('refreshToken');
    }
  } catch (_) { }
}

export function getRefreshToken(): string | null {
  try { return localStorage.getItem('refreshToken'); } catch (_) { return null; }
}

export function clearTokens(): void {
  _accessToken = null;
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('token');
  } catch (_) { }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const url: string = originalRequest?.url || '';

    if (status === 403) {
      const code = error?.response?.data?.code;
      if (code === 'ACCOUNT_SUSPENDED') {
        clearTokens();
        try {
          window.dispatchEvent(new CustomEvent('auth:suspended'));
        } catch (_) { }
        return Promise.reject(error);
      }
    }

    if (
      status === 401 &&
      !originalRequest._retry &&
      !url.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        isRefreshing = false;
        handleUnauthorized();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { token: newToken, refreshToken: newRefreshToken } = response.data;

        setAccessToken(newToken);
        setRefreshToken(newRefreshToken);

        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        processQueue(null, newToken);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        handleUnauthorized();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

function handleUnauthorized() {
  clearTokens();
  try {
    window.dispatchEvent(new Event('auth:unauthorized'));
  } catch (_) { }
}

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

export const authAPI = {
  refreshToken: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  logout: (refreshToken: string | null) =>
    api.post('/auth/logout', { refreshToken }),

  
  exchangeCode: (code: string) =>
    api.get(`/auth/exchange-code?code=${encodeURIComponent(code)}`),
};

export const userAPI = {
  getProfile: () => api.get('/users/profile'),

  updateProfile: (data: any) => api.put('/users/profile', data),

  updateProfileWithImage: (formData: FormData) =>
    api.put('/users/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

export const projectsAPI = {
  getAll: (params?: Record<string, any>) => api.get('/projects', params ? { params } : undefined),

  getActive: (params?: Record<string, any>) => api.get('/projects/active', params ? { params } : undefined),

  getFeatured: () => api.get('/projects/featured'),

  getById: (id: string) => api.get(`/projects/${id}`),

  create: (data: any) => api.post('/projects', data),

  update: (id: string, data: any) => api.put(`/projects/${id}`, data),

  updateStatus: (id: string, status: 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED') =>
    api.put(`/projects/${id}/status`, { status }),

  delete: (id: string) => api.delete(`/projects/${id}`),

  search: (keyword: string) => api.get(`/projects/search?keyword=${encodeURIComponent(keyword)}`),
};

export const donationsAPI = {
  getAll: (params?: Record<string, any>) => api.get('/donations', params ? { params } : undefined),
  getUserDonations: (params?: Record<string, any>) => api.get('/donations/my-donations', params ? { params } : undefined),
  getProjectDonations: (projectId: string) => api.get(`/donations/project/${projectId}`),
  getById: (id: string) => api.get(`/donations/${id}`),

  getStats: () => api.get('/donations/stats'),

  initiatePayment: (data: {
    projectId: number;
    amount: number;
    message?: string;
    selected_blocks?: string[];
    selected_districts?: string[];
    donationType: 'individual' | 'areaWise';
    name?: string;
    phone?: string;
    email?: string;
    postal_code?: string;
    state?: string;
    district?: string;
    pan_card_no?: string;
    purpose?: string;
    referencer_id?: string;
    referencerId?: string;
  }) => api.post('/donations/create-order', data),

  verifyPayment: (data: any) => api.post('/donations/verify-payment', data),

  emailReceipt: (id: string) => api.post(`/donations/${id}/email-receipt`),

  getReceiptHtml: (id: string) => api.get(`/donations/${id}/receipt`, { responseType: 'text' }),

  exportData: (params: { startDate: string; endDate: string; format: string }) => {
    const mapped: Record<string, string> = {};
    if (params.startDate) mapped.date_from = params.startDate;
    if (params.endDate)   mapped.date_to   = params.endDate;
    return api.get('/donations/export', { params: mapped, responseType: 'blob' });
  },
};

export const dashboardAPI = {
  getUserStats: () => api.get('/dashboard/user-stats'),

  getAdminStats: () => api.get('/dashboard/admin-stats'),

  getReferencerStats: () => api.get('/dashboard/referencer-stats'),

  getRecentDonations: () => api.get('/dashboard/recent-donations'),

  getPublicStats: () => api.get('/dashboard/public-stats'),
};

export const usersAPI = {
  getAll: (params?: Record<string, any>) => api.get('/users', params ? { params } : undefined),

  getStats: () => api.get('/users/stats'),

  
  getFullProfile: (id: string | number) => api.get(`/users/${id}`),

  update: (id: string, data: any) => api.put(`/users/${id}`, data),

  updateRole: (id: string, role: string) =>
    api.put(`/users/${id}/role`, { role }),

  verify: (id: string) => api.put(`/users/${id}/verify`),

  block: (id: string) => api.put(`/users/${id}/block`),

  unblock: (id: string) => api.put(`/users/${id}/unblock`),

  deleteUser: (id: string) => api.delete(`/users/${id}`),

  
  exportCsv: () => api.get('/users/export', { responseType: 'blob', timeout: 120000 }),
};

export const blocksAPI = {
  getAll: (activeOnly = false) => api.get(`/blocks${activeOnly ? '?active=true' : ''}`),

  getById: (id: string) => api.get(`/blocks/${id}`),

  getDistricts: () => api.get('/blocks/districts'),

  getByDistrict: (district: string) => api.get(`/blocks/by-district?district=${encodeURIComponent(district)}`),

  create: (data: {
    name: string;
    name_ar?: string;
    description?: string;
    is_active?: boolean;
  }) => api.post('/blocks', data),

  update: (id: string, data: {
    name?: string;
    name_ar?: string;
    description?: string;
    is_active?: boolean;
  }) => api.put(`/blocks/${id}`, data),

  delete: (id: string) => api.delete(`/blocks/${id}`),
};

export const ambassadorsAPI = {
  getAll: () => api.get('/ambassadors'),
  search: (q: string) => api.get(`/ambassadors/search?q=${encodeURIComponent(q)}`),
  getAdminAll: () => api.get('/ambassadors/admin/all'),

  applyForAmbassador: (data: {
    amb_designation?: string;
    amb_organization?: string;
    amb_location?: string;
    amb_statement?: string;
    amb_letter_scan?: string;
  }) => api.put('/users/apply-ambassador', data),

  getPendingApplications: (status = 'PENDING') => api.get(`/users/ambassador-applications?status=${status}`),
  approveAmbassador: (userId: string | number) => api.put(`/users/${userId}/approve-ambassador`, {}),
  rejectAmbassador: (userId: string | number, reason?: string) => api.put(`/users/${userId}/reject-ambassador`, { rejection_reason: reason || '' }),
  revokeAmbassador: (userId: string | number) => api.put(`/users/${userId}/revoke-ambassador`, {}),

  getMyDonors: () => api.get('/ambassadors/my-donors'),
  acceptDonor: (userId: string | number) => api.put(`/ambassadors/donors/${userId}/accept`, {}),
  rejectDonor: (userId: string | number) => api.put(`/ambassadors/donors/${userId}/reject`, {}),

  getAmbassadorStats: () => api.get('/dashboard/ambassador-stats'),
};

export const subscriptionsAPI = {
  create: (data: {
    projectId: number;
    amount: number;
    planId?: string;
    frequency: string;
    selectedBlocks?: string[];
    selectedDistricts?: string[];
    purpose?: string;
  }) => api.post('/subscriptions/create', data),

  getMySubscriptions: () => api.get('/subscriptions/my-subscriptions'),

  cancel: (id: string) => api.post(`/subscriptions/cancel/${id}`),
};

export const alumniAPI = {
  search: (q: string) => api.get(`/alumni/search?q=${encodeURIComponent(q)}`),

  getCenters: () => api.get('/alumni/centers'),
  getBatchesByCenter: (centerName: string) =>
    api.get(`/alumni/centers/${encodeURIComponent(centerName)}/batches`),

  getMyRequest: () => api.get('/alumni/my-request'),

  submitCustomRequest: (data: { full_name: string; center_name: string; batch_code: string }) =>
    api.post('/alumni/custom-request', data),

  adminGetRequests: (status?: string) =>
    api.get(`/alumni/requests${status && status !== 'all' ? `?status=${status}` : ''}`),
  adminAccept: (id: number) => api.patch(`/alumni/requests/${id}/accept`, {}),
  adminReject: (id: number, rejection_reason?: string) =>
    api.patch(`/alumni/requests/${id}/reject`, { rejection_reason }),
};

export const kycAPI = {
  getStatus: () => api.get('/donor/kyc-status'),
  recalculate: () => api.post('/donor/kyc-status/recalculate'),
};

export default api;
